import type { LegendListProps } from '@legendapp/list/react-native';
import Avatar from '-/Custom/Avatar';
import * as Placeholder from '-/Custom/Placeholder';
import Icon from '-/Unistyles/Icon';
import { useLocalSearchParams, useRouter } from 'expo-router';
import type { ReactElement } from 'react';
import { View } from 'react-native';
import { StyleSheet, withUnistyles } from 'react-native-unistyles';
import * as EventRsvpPicker from '@/components/Event/EventRsvpPicker';
import {
  isRichTextEmpty,
  richTextToPlainText,
} from '@/components/RichText/RichText.utils';
import RichTextDisplay from '@/components/RichText/RichTextDisplay';
import Badge from '@/components/UI/Badge';
import Box from '@/components/UI/Box';
import Button from '@/components/UI/Button';
import PressableRow from '@/components/UI/PressableRow';
import Text from '@/components/UI/Text';
import List from '@/components/Unistyles/List';
import { type FragmentOf, graphql, readFragment } from '@/gql/graphql';

export type EventDetailHomePathname =
  | '/event/[eventId]'
  | '/organization/event/[eventId]';

export type EventTeamMemberPathname =
  | '/event/team/member/[personId]'
  | '/organization/event/team/member/[personId]';

type RsvpStatus = ReturnType<typeof graphql.scalar<'rsvp_status_enum'>>;
type MemberType = ReturnType<typeof graphql.scalar<'member_type_enum'>>;

type BadgeAssignment = {
  id: string;
  previewValue: string | null;
  badge: {
    color: string;
    title: string;
  };
};

type TeamHeaderRow = {
  type: 'team';
  key: string;
  avatarId: string;
  color: string;
  teamId: string;
  title: string;
  yes: number;
  maybe: number;
  no: number;
  noResponse: number;
};

type MemberRow = {
  type: 'member';
  key: string;
  avatarId: string;
  badgeAssignmentCount: number;
  badgeAssignments: BadgeAssignment[];
  canEditRsvp: boolean;
  name: string;
  memberType: MemberType;
  notes: string;
  personId: string;
  rsvp: RsvpStatus | null;
  teamId: string;
};

type DividerRow = {
  type: 'divider';
  key: string;
  label: string;
};

type YourRsvpPerson = {
  key: string;
  avatarId: string;
  color: string;
  name: string;
  notes: string;
  personId: string;
  rsvp: RsvpStatus | null;
};

type YourRsvpsRow = {
  type: 'yourRsvps';
  key: string;
  people: YourRsvpPerson[];
};

type EventDetailHomeRow = TeamHeaderRow | MemberRow | DividerRow | YourRsvpsRow;

export const EventDetailHomeRosterPriorityFragment = graphql(/* GraphQL */ `
  fragment EventDetailHomeRosterPriority on query_root {
    yourRsvpPerson: person_by_pk(id: $personId) {
      rsvp_editor_access(limit: 1, where: { event_id: { _eq: $eventId } }) {
        person_id
      }
      id
      name
      avatar_id
      color
      event_rsvp: rsvps(where: { event_id: { _eq: $eventId } }, limit: 1) {
        notes
        status
      }
    }
    managedRsvpPeople: person_manager(
      order_by: { person: { name: asc } }
      where: {
        manager_id: { _eq: $personId }
        person_event_participants: { event_id: { _eq: $eventId } }
      }
    ) {
      person_id
      person {
        id
        name
        avatar_id
        color
        event_rsvp: rsvps(where: { event_id: { _eq: $eventId } }, limit: 1) {
          notes
          status
        }
      }
    }
  }
`);

export const EventDetailHomeTeamMemberFragment = graphql(/* GraphQL */ `
  fragment EventDetailHomeTeamMember on person_team {
    team_id
    person_id
    member_type
    badge_assignments(
      where: { event_id: { _is_null: true } }
      order_by: { updated_at: desc }
      limit: 3
    ) {
      id
      preview_value
      badge {
        color
        title
      }
    }
    badge_assignments_aggregate(where: { event_id: { _is_null: true } }) {
      aggregate {
        count
      }
    }
    person {
      id
      name
      avatar_id
      rsvp_editor_access(limit: 1, where: { event_id: { _eq: $eventId } }) {
        person_id
      }
      event_rsvp: rsvps(where: { event_id: { _eq: $eventId } }, limit: 1) {
        event_id
        person_id
        notes
        status
      }
    }
  }
`);

export const EventDetailHomeTeamsFragment = graphql(/* GraphQL */ `
  fragment EventDetailHomeTeams on event {
    event_teams(
      limit: $teamLimit
      order_by: [
        { team: { on_team: desc } }
        { is_home: asc }
        { team: { title: asc } }
      ]
    ) {
      team_id
      rsvp_summary {
        event_id
        team_id
        yes
        maybe
        no
      }
      team {
        id
        title
        avatar_id
        home_primary_color
        permissions
        participant_count: team_people_aggregate(
          where: { member_type: { _eq: participant } }
        ) {
          aggregate {
            count
          }
        }
      }
    }
  }
`);

export const EventDetailHomeRosterFragment = graphql(/* GraphQL */ `
  fragment EventDetailHomeRoster on query_root {
    eventDetailHomeRoster: person_team(
      where: { team: { team_events: { event_id: { _eq: $eventId } } } }
      order_by: [
        { team_id: asc }
        { person: { name: asc } }
        { person_id: asc }
      ]
    ) {
      ...EventDetailHomeTeamMember
    }
  }
`, [EventDetailHomeTeamMemberFragment]);

const rsvpOrder: Record<RsvpStatus | 'none', number> = {
  yes: 0,
  maybe: 1,
  no: 2,
  none: 3,
};

function normalizeRsvp(value: string | null | undefined): RsvpStatus | null {
  if (value === 'yes' || value === 'maybe' || value === 'no') {
    return value;
  }

  return null;
}

function normalizeMemberType(
  value: string | null | undefined,
): MemberType | null {
  if (value === 'participant' || value === 'non_participant') {
    return value;
  }

  return null;
}

function compareMembers(first: MemberRow, second: MemberRow) {
  const firstRank = rsvpOrder[first.rsvp ?? 'none'];
  const secondRank = rsvpOrder[second.rsvp ?? 'none'];

  return firstRank - secondRank || first.name.localeCompare(second.name);
}

export function getRows(
  query: FragmentOf<typeof EventDetailHomeTeamsFragment> | null | undefined,
  priorityQuery:
    | FragmentOf<typeof EventDetailHomeRosterPriorityFragment>
    | null
    | undefined,
  rosterQuery:
    | FragmentOf<typeof EventDetailHomeRosterFragment>
    | null
    | undefined,
) {
  const fragment = readFragment(EventDetailHomeTeamsFragment, query);
  const rosterQueryFragment = readFragment(
    EventDetailHomeRosterFragment,
    rosterQuery,
  );
  const rosterFragment = readFragment(
    EventDetailHomeTeamMemberFragment,
    rosterQueryFragment?.eventDetailHomeRoster ?? [],
  );
  const priorityFragment = readFragment(
    EventDetailHomeRosterPriorityFragment,
    priorityQuery,
  );
  const rows: EventDetailHomeRow[] = [];

  if (fragment) {
    const yourRsvps: YourRsvpPerson[] = [];
    const addedPersonIds = new Set<string>();
    const currentPerson = priorityFragment?.yourRsvpPerson;

    if (currentPerson?.rsvp_editor_access.length) {
      addedPersonIds.add(currentPerson.id);
      yourRsvps.push({
        key: `your-rsvp-${currentPerson.id}`,
        avatarId: currentPerson.avatar_id ?? '',
        color: currentPerson.color ?? '',
        name: currentPerson.name ?? '',
        notes: currentPerson.event_rsvp[0]?.notes ?? '',
        personId: currentPerson.id,
        rsvp: normalizeRsvp(currentPerson.event_rsvp[0]?.status),
      });
    }

    for (const managedPerson of priorityFragment?.managedRsvpPeople ?? []) {
      const person = managedPerson.person;
      if (addedPersonIds.has(person.id)) {
        continue;
      }

      addedPersonIds.add(person.id);
      yourRsvps.push({
        key: `your-rsvp-${person.id}`,
        avatarId: person.avatar_id ?? '',
        color: person.color ?? '',
        name: person.name ?? '',
        notes: person.event_rsvp[0]?.notes ?? '',
        personId: person.id,
        rsvp: normalizeRsvp(person.event_rsvp[0]?.status),
      });
    }

    if (yourRsvps.length > 0) {
      rows.push({
        type: 'yourRsvps',
        key: 'your-rsvps',
        people: yourRsvps,
      });
    }
  }

  for (const eventTeam of fragment?.event_teams ?? []) {
    const team = eventTeam.team;
    const members = rosterFragment
      .filter((teamPerson) => teamPerson.team_id === team.id)
      .flatMap<MemberRow>((teamPerson) => {
        const memberType = normalizeMemberType(teamPerson.member_type);
        const person = teamPerson.person;
        const personId = teamPerson.person_id;

        if (!memberType || !person || !personId) {
          return [];
        }

        return [
          {
            type: 'member',
            key: `member-${team.id}-${personId}`,
            avatarId: person.avatar_id ?? '',
            badgeAssignmentCount:
              teamPerson.badge_assignments_aggregate.aggregate?.count ?? 0,
            badgeAssignments: teamPerson.badge_assignments.map(
              (badgeAssignment) => ({
                id: badgeAssignment.id,
                previewValue: badgeAssignment.preview_value,
                badge: {
                  color: badgeAssignment.badge.color,
                  title: badgeAssignment.badge.title,
                },
              }),
            ),
            canEditRsvp: person.rsvp_editor_access.length > 0,
            memberType,
            name: person.name ?? '',
            notes: person.event_rsvp[0]?.notes ?? '',
            personId,
            rsvp: normalizeRsvp(person.event_rsvp[0]?.status),
            teamId: team.id,
          },
        ];
      })
      .sort(compareMembers);

    if (members.length === 0) {
      continue;
    }

    const participants = members
      .filter((member) => member.memberType === 'participant')
      .sort(compareMembers);
    const nonParticipants = members
      .filter((member) => member.memberType === 'non_participant')
      .sort(compareMembers);
    const yes =
      eventTeam.rsvp_summary?.yes ??
      participants.filter((member) => member.rsvp === 'yes').length;
    const maybe =
      eventTeam.rsvp_summary?.maybe ??
      participants.filter((member) => member.rsvp === 'maybe').length;
    const no =
      eventTeam.rsvp_summary?.no ??
      participants.filter((member) => member.rsvp === 'no').length;
    const participantCount =
      team.participant_count.aggregate?.count ?? participants.length;
    rows.push({
      type: 'team',
      key: `team-${team.id}`,
      avatarId: team.avatar_id ?? '',
      color: team.home_primary_color ?? '',
      teamId: team.id,
      title: team.title,
      yes,
      maybe,
      no,
      noResponse: Math.max(participantCount - yes - maybe - no, 0),
    });
    rows.push(...participants);

    if (nonParticipants.length > 0) {
      rows.push({
        type: 'divider',
        key: `non-participant-divider-${team.id}`,
        label: 'Non-participants',
      });
      rows.push(...nonParticipants);
    }
  }

  return rows;
}

function TeamRsvpCounts({ row }: { row: TeamHeaderRow }) {
  return (
    <Box direction="horizontal" space="sm">
      <Text size="sm" variant="success">
        {row.yes} Yes
      </Text>
      <Text size="sm" variant="warning">
        {row.maybe} Maybe
      </Text>
      <Text size="sm" variant="error">
        {row.no} No
      </Text>
      <Text size="sm" variant="typographySecondary">
        {row.noResponse} No Response
      </Text>
    </Box>
  );
}

function TeamHeader({ eventId, row }: { eventId: string; row: TeamHeaderRow }) {
  const router = useRouter();

  return (
    <View style={styles.teamHeader} testID={`EventTeamSection-${row.teamId}`}>
      <PressableRow
        accessibilityLabel={`Open ${row.title}`}
        onPress={() => {
          router.push({
            pathname: './team/[teamId]',
            params: { eventId, teamId: row.teamId },
          });
        }}
        style={styles.teamHeaderPressable}
      >
        <Avatar
          color={row.color}
          id={row.avatarId}
          name={row.title}
          size="xl"
        />
        <Box flex minWidth={0} space="xs">
          <Text fontWeight="semibold" numberOfLines={2} size="2xl">
            {row.title}
          </Text>
          <TeamRsvpCounts row={row} />
        </Box>
        <Icon
          name="chevron-forward"
          size="sm"
          testID={`EventTeamSectionCaret-${row.teamId}`}
          variant="typographySecondary"
        />
      </PressableRow>
    </View>
  );
}

const NeutralMemberAvatar = withUnistyles(Avatar, (theme) => ({
  backgroundColor: 'transparent',
  color: theme.colors.surfaceMuted,
  ringColor: theme.colors.borderSecondary,
}));

const YesMemberAvatar = withUnistyles(Avatar, (theme) => ({
  backgroundColor: 'transparent',
  color: theme.colors.surfaceMuted,
  ringColor: theme.colors.success,
}));

const MaybeMemberAvatar = withUnistyles(Avatar, (theme) => ({
  backgroundColor: 'transparent',
  color: theme.colors.surfaceMuted,
  ringColor: theme.colors.warning,
}));

const NoMemberAvatar = withUnistyles(Avatar, (theme) => ({
  backgroundColor: 'transparent',
  color: theme.colors.surfaceMuted,
  ringColor: theme.colors.error,
}));

function MemberAvatar({
  row,
}: {
  row: Pick<MemberRow, 'avatarId' | 'name' | 'rsvp'>;
}) {
  if (row.rsvp === 'yes') {
    return (
      <YesMemberAvatar
        id={row.avatarId}
        name={row.name}
        ringWidth={2}
        size="lg"
      />
    );
  }

  if (row.rsvp === 'maybe') {
    return (
      <MaybeMemberAvatar
        id={row.avatarId}
        name={row.name}
        ringWidth={2}
        size="lg"
      />
    );
  }

  if (row.rsvp === 'no') {
    return (
      <NoMemberAvatar
        id={row.avatarId}
        name={row.name}
        ringWidth={2}
        size="lg"
      />
    );
  }

  return (
    <NeutralMemberAvatar
      id={row.avatarId}
      name={row.name}
      ringWidth={2}
      size="lg"
    />
  );
}

function YourRsvpItem({
  eventId,
  row,
}: {
  eventId: string;
  row: YourRsvpPerson;
}) {
  const router = useRouter();
  const hasNotes = !isRichTextEmpty(row.notes);
  const openRsvpEditor = () => {
    router.push({
      pathname: '/event/[eventId]/rsvp',
      params: { eventId, subjectPersonId: row.personId },
    });
  };

  return (
    <Box
      alignItems="center"
      direction="horizontal"
      space="lg"
      style={styles.yourRsvpRow}
      testID={`EventYourRsvp-${row.personId}`}
    >
      <Avatar color={row.color} id={row.avatarId} name={row.name} size="xl" />
      <Box flex minWidth={0} space="xs">
        <Text fontWeight="semibold" numberOfLines={2} size="2xl">
          {row.name}
        </Text>
        {hasNotes ? (
          <RichTextDisplay
            html={row.notes}
            numberOfLines={1}
            size="xs"
            variant="typographySecondary"
          />
        ) : null}
      </Box>
      <EventRsvpPicker.Button
        accessibilityLabel={`Edit RSVP for ${row.name}`}
        isSelected
        onPress={openRsvpEditor}
        rsvp={row.rsvp}
        size="lg"
        testID={`EventYourRsvpButton-${row.personId}`}
      />
      <View style={styles.hiddenCaret}>
        <Icon name="chevron-forward" size="sm" variant="typographySecondary" />
      </View>
    </Box>
  );
}

function YourRsvps({ eventId, row }: { eventId: string; row: YourRsvpsRow }) {
  return (
    <Box my="sm" space="sm" testID="EventYourRsvps">
      {row.people.map((person) => (
        <YourRsvpItem eventId={eventId} key={person.key} row={person} />
      ))}
    </Box>
  );
}

function MemberBadges({ row }: { row: MemberRow }) {
  const extraBadgeCount =
    row.badgeAssignmentCount - row.badgeAssignments.length;

  if (row.badgeAssignments.length === 0 && extraBadgeCount <= 0) {
    return null;
  }

  return (
    <Box direction="horizontal" wrap space="xs">
      {row.badgeAssignments.map((badgeAssignment) => (
        <Badge
          color={badgeAssignment.badge.color}
          key={badgeAssignment.id}
          testID="EventDetailRosterTeamTag"
        >
          <Badge.Text>
            {badgeAssignment.previewValue || badgeAssignment.badge.title}
          </Badge.Text>
        </Badge>
      ))}
      {extraBadgeCount > 0 ? (
        <Badge>
          <Badge.Text>+{extraBadgeCount}</Badge.Text>
        </Badge>
      ) : null}
    </Box>
  );
}

function MemberItem({
  eventId,
  memberPathname,
  row,
}: {
  eventId: string;
  memberPathname: EventTeamMemberPathname;
  row: MemberRow;
}) {
  const router = useRouter();
  const hasNotes = !isRichTextEmpty(row.notes);
  const canEditRsvp = row.canEditRsvp;
  const openRsvpEditor = () => {
    router.push({
      pathname: '/event/[eventId]/rsvp',
      params: { eventId, subjectPersonId: row.personId },
    });
  };
  const openRsvpNote = () => {
    router.push({
      pathname: '/event/[eventId]/rsvp-note/[personId]',
      params: { eventId, personId: row.personId },
    });
  };
  const openMember = () => {
    router.push({
      pathname: memberPathname,
      params: { eventId, personId: row.personId, teamId: row.teamId },
    });
  };

  return (
    <PressableRow
      accessibilityLabel={`Open ${row.name}`}
      onPress={openMember}
      style={styles.memberRow}
      testID={`EventTeamMember-${row.teamId}-${row.personId}`}
    >
      <MemberAvatar row={row} />
      <Box flex minWidth={0} space="xs">
        <Text flexShrink={1} fontWeight="medium" numberOfLines={2}>
          {row.name}
        </Text>
        {hasNotes ? (
          <Button
            accessibilityLabel={`View RSVP note for ${row.name}`}
            alignSelf="flex-start"
            appearance="outline"
            maxWidth="100%"
            onPress={(event) => {
              event.stopPropagation();
              openRsvpNote();
            }}
            size="xs"
            testID={`EventRsvpNotePreview-${row.personId}`}
            variant="secondary"
          >
            <Button.Text flexShrink={1} numberOfLines={1} size="xs">
              {richTextToPlainText(row.notes)}
            </Button.Text>
          </Button>
        ) : null}
        <MemberBadges row={row} />
      </Box>
      <EventRsvpPicker.Button
        accessibilityLabel={
          canEditRsvp ? `Edit RSVP for ${row.name}` : `RSVP for ${row.name}`
        }
        disabled={!canEditRsvp}
        isSelected
        onPress={(event) => {
          event.stopPropagation();
          openRsvpEditor();
        }}
        rsvp={row.rsvp}
        size="lg"
        testID={`EventTeamMemberRsvp-${row.personId}`}
      />
      <Icon name="chevron-forward" size="sm" variant="typographySecondary" />
    </PressableRow>
  );
}

function RosterDivider({ row }: { row: DividerRow }) {
  return (
    <Box
      alignItems="center"
      direction="horizontal"
      space="sm"
      style={styles.dividerRow}
    >
      <View style={styles.dividerLine} />
      <Text
        fontWeight="semibold"
        size="2xs"
        style={styles.dividerLabel}
        variant="typographySecondary"
      >
        {row.label}
      </Text>
      <View style={styles.dividerLine} />
    </Box>
  );
}

function RosterEmptyState({ hasTeams }: { hasTeams: boolean }) {
  return (
    <Placeholder.Container style={styles.emptyState}>
      <Placeholder.Icon name="people-outline" />
      <Placeholder.Text>
        {hasTeams
          ? 'Team rosters aren’t available.'
          : 'No teams have been added to this event.'}
      </Placeholder.Text>
      {hasTeams ? (
        <Placeholder.Text size="md">
          You don’t have access to these rosters, or no members have been added
          yet.
        </Placeholder.Text>
      ) : null}
    </Placeholder.Container>
  );
}

export default function EventDetailHomeTeams({
  listHeader,
  priorityQuery,
  query,
  rosterQuery,
  teamMemberPathname,
}: {
  listHeader: ReactElement | null;
  priorityQuery:
    | FragmentOf<typeof EventDetailHomeRosterPriorityFragment>
    | null
    | undefined;
  query: FragmentOf<typeof EventDetailHomeTeamsFragment> | null | undefined;
  rosterQuery:
    | FragmentOf<typeof EventDetailHomeRosterFragment>
    | null
    | undefined;
  teamMemberPathname: EventTeamMemberPathname;
}) {
  const { eventId = '' } = useLocalSearchParams<{ eventId?: string }>();
  const teamFragment = readFragment(EventDetailHomeTeamsFragment, query);
  const rows = getRows(query, priorityQuery, rosterQuery);
  const shouldShowRosterEmptyState = query !== null && query !== undefined;
  const hasTeams = (teamFragment?.event_teams.length ?? 0) > 0;
  const hasVisibleTeams = rows.some((row) => row.type === 'team');
  const stickyHeaderIndices = rows.reduce<number[]>((indices, row, index) => {
    if (row.type === 'team') {
      indices.push(index);
    }
    return indices;
  }, []);
  const renderItem: LegendListProps<EventDetailHomeRow>['renderItem'] = ({
    item,
  }) => {
    if (item.type === 'team') {
      return <TeamHeader eventId={eventId} row={item} />;
    }

    if (item.type === 'divider') {
      return <RosterDivider row={item} />;
    }

    if (item.type === 'yourRsvps') {
      return <YourRsvps eventId={eventId} row={item} />;
    }

    return (
      <MemberItem
        eventId={eventId}
        memberPathname={teamMemberPathname}
        row={item}
      />
    );
  };

  return (
    <Box flex testID="EventDetailPeopleList">
      <List<EventDetailHomeRow>
        contentContainerStyle={styles.listContent}
        data={rows}
        estimatedItemSize={72}
        getItemType={(row) => row.type}
        keyExtractor={(row) => row.key}
        keyboardScroll={false}
        ListEmptyComponent={
          shouldShowRosterEmptyState ? (
            <RosterEmptyState hasTeams={hasTeams} />
          ) : null
        }
        ListFooterComponent={
          shouldShowRosterEmptyState && rows.length > 0 && !hasVisibleTeams ? (
            <RosterEmptyState hasTeams={hasTeams} />
          ) : null
        }
        ListHeaderComponent={listHeader}
        recycleItems
        renderItem={renderItem}
        stickyHeaderIndices={stickyHeaderIndices}
      />
    </Box>
  );
}

const styles = StyleSheet.create((theme) => ({
  listContent: {
    paddingBottom: theme.size(8),
  },
  emptyState: {
    minHeight: theme.size(48),
    paddingVertical: theme.size(8),
  },
  dividerRow: {
    minHeight: theme.size(6),
    paddingHorizontal: theme.size(4),
  },
  dividerLine: {
    backgroundColor: theme.colors.borderMuted,
    flex: 1,
    height: theme.size(0.5),
  },
  dividerLabel: {
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  teamHeader: {
    backgroundColor: theme.colors.background,
    borderBottomColor: theme.colors.borderMuted,
    borderBottomWidth: theme.size(0.5),
    borderTopColor: theme.colors.borderMuted,
    borderTopWidth: theme.size(0.5),
  },
  teamHeaderPressable: {
    minHeight: theme.size(16),
    paddingHorizontal: theme.size(4),
    paddingVertical: theme.size(3),
  },
  memberRow: {
    minHeight: theme.size(16),
    paddingHorizontal: theme.size(4),
    paddingVertical: theme.size(3),
  },
  yourRsvpRow: {
    minHeight: theme.size(16),
    paddingHorizontal: theme.size(4),
    paddingVertical: theme.size(3),
  },
  hiddenCaret: {
    opacity: 0,
  },
}));
